"use client";

/**
 * ProgramContext — current-program selector for multi-tenant usage.
 *
 * Stores active programId in localStorage so it persists across reloads.
 * Falls back to first program in user.programs[] or "demo-cs" (legacy).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useAllPrograms, type ProgramInfo } from "./programs";
import { useAuth } from "./auth";

const STORAGE_KEY = "verification-msu/activeProgramId";
const LEGACY_DEFAULT = "demo-cs";

// Module-level mirror of the active programId, kept in sync by ProgramProvider's
// effect. Pages that haven't been refactored to call useProgram() directly can
// still import this and stay correct after the user switches programs.
let _activeProgramId: string = LEGACY_DEFAULT;
if (typeof window !== "undefined") {
  try { _activeProgramId = window.localStorage.getItem(STORAGE_KEY) ?? LEGACY_DEFAULT; }
  catch { /* ignore */ }
}
/** Get the currently-selected programId. Updates as ProgramProvider's state changes. */
export function getActiveProgramId(): string { return _activeProgramId; }

interface ProgramCtx {
  programId: string;
  programInfo: ProgramInfo | null;
  availablePrograms: ProgramInfo[];
  isLoading: boolean;
  switchProgram: (id: string) => void;
}

const Ctx = createContext<ProgramCtx | null>(null);

export function ProgramProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const allPrograms = useAllPrograms();
  const [programId, setProgramId] = useState<string>(LEGACY_DEFAULT);
  const [hydrated, setHydrated] = useState(false);

  // On first mount, hydrate from localStorage
  useEffect(() => {
    if (hydrated) return;
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setProgramId(saved);
      } else if (profile?.programs && profile.programs.length > 0) {
        setProgramId(profile.programs[0]);
      }
    }
    setHydrated(true);
  }, [hydrated, profile?.programs]);

  // Validate: if the selected programId doesn't exist in availablePrograms,
  // fall back to the first available one.
  useEffect(() => {
    if (!hydrated) return;
    if (allPrograms.isLoading) return;
    const list = allPrograms.data ?? [];
    if (list.length === 0) return;
    const exists = list.some((p) => p.id === programId);
    if (!exists) {
      const first = list[0].id;
      setProgramId(first);
      if (typeof window !== "undefined") localStorage.setItem(STORAGE_KEY, first);
    }
  }, [hydrated, allPrograms.isLoading, allPrograms.data, programId]);

  function switchProgram(id: string) {
    setProgramId(id);
    _activeProgramId = id;
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, id);
      // Force a full reload so pages that read PROGRAM_ID at module-init
      // (legacy pages not yet refactored) pick up the new value.
      window.location.reload();
    }
  }

  // Keep module-level mirror in sync
  useEffect(() => { _activeProgramId = programId; }, [programId]);

  const programInfo = (allPrograms.data ?? []).find((p) => p.id === programId) ?? null;

  return (
    <Ctx.Provider
      value={{
        programId,
        programInfo,
        availablePrograms: allPrograms.data ?? [],
        isLoading: allPrograms.isLoading,
        switchProgram,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

/** Hook to access current program context. Must be inside ProgramProvider. */
export function useProgram(): ProgramCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Graceful fallback for components rendered outside the provider during tests
    // or for static-export build-time pre-rendering.
    return {
      programId: LEGACY_DEFAULT,
      programInfo: null,
      availablePrograms: [],
      isLoading: false,
      switchProgram: () => {},
    };
  }
  return ctx;
}

/** Compact dropdown to switch programs in the navbar. */
export function ProgramSwitcher() {
  const { programId, availablePrograms, switchProgram, isLoading } = useProgram();
  if (isLoading) return null;
  if (availablePrograms.length <= 1) {
    // Only one program — show name as static label
    const p = availablePrograms[0];
    if (!p) return null;
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-brand-50 text-brand-700" title={p.nameTh}>
        {p.emoji ?? "📘"} {p.degree} {p.nameTh.length > 30 ? p.nameTh.slice(0, 28) + "…" : p.nameTh}
      </span>
    );
  }
  return (
    <select
      value={programId}
      onChange={(e) => switchProgram(e.target.value)}
      className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white hover:bg-slate-50"
      title="สลับหลักสูตร"
    >
      {availablePrograms.map((p) => (
        <option key={p.id} value={p.id}>
          {p.emoji ?? "📘"} {p.degree} {p.nameTh}
        </option>
      ))}
    </select>
  );
}
